/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/refs */
import { faSave } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, CircularProgress, Stack } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { AttendanceLogEntryEditor } from "../../../components";
import { FormProvider, FormState, useForm } from "../../../components/FormProvider";
import { AttendanceLogEntry } from "../../../models";
import {
  useDeleteAttendanceLogEntryMutation,
  useGetAllAttendanceLogEntriesFilteredQuery,
  useGetAllChildrenFilteredQuery,
  useSaveAttendanceLogEntryMutation,
} from "../../../redux/apiStore";

export function EditAttendanceForm(props: { date: Dayjs; onBack: () => void }) {
  const { date, onBack } = props;
  const [saveAttendanceLogEntry] = useSaveAttendanceLogEntryMutation();
  const [deleteAttendanceLogEntry] = useDeleteAttendanceLogEntryMutation();

  const handleSave = async (form: FormState) => {
    form.setLoading(true);
    try {
      await Promise.all(
        pendingLogsRef.current.map((log) => {
          if (log.deleted && log.id > 0) {
            return deleteAttendanceLogEntry({ id: log.id }).unwrap();
          } else if (!log.deleted) {
            return saveAttendanceLogEntry(log).unwrap();
          }
          return Promise.resolve();
        }),
      );
      form.notifySuccess("Attendance saved");
      onBack();
    } catch (err) {
      form.notifyError("Failed to save attendance", err);
    } finally {
      form.setLoading(false);
    }
  };

  const handleCancel = (_form: FormState) => {
    onBack();
  };

  const pendingLogsRef = useRef<AttendanceLogEntry[]>([]);

  return (
    <FormProvider fullHeight onSave={handleSave} onCancel={handleCancel} onDelete={() => {}} showControls={false}>
      <AttendanceFormBody date={date} logsRef={pendingLogsRef} />
    </FormProvider>
  );
}

function AttendanceFormBody(props: { date: Dayjs; logsRef: React.MutableRefObject<AttendanceLogEntry[]> }) {
  const { date, logsRef } = props;
  const form = useForm();
  const selectedDate = date.format("YYYY-MM-DD");
  const getAttendanceLogEntries = useGetAllAttendanceLogEntriesFilteredQuery({
    rangeStartDate: date.subtract(1, "day").startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    rangeEndDate: date.add(1, "day").endOf("day").format("YYYY-MM-DD HH:mm:ss"),
  });
  const children = useGetAllChildrenFilteredQuery({ showInactive: false });

  const activeChildIds = children?.data?.filter((x) => x.isActive).map((x) => x.id) ?? [];
  const childIds = [...activeChildIds, ...(getAttendanceLogEntries?.data?.map((x) => x.childId) ?? [])];
  const childrenToList = _.orderBy(children?.data?.filter((x) => childIds.includes(x.id)) ?? [], "firstName", "asc");
  const isFetching = getAttendanceLogEntries.isFetching || children.isFetching;
  const [logs, setLogs] = useState<AttendanceLogEntry[]>([]);

  useEffect(() => {
    const all = getAttendanceLogEntries.data ?? [];
    const filtered = all.filter((x) => {
      if (!x.timeInCentralDateTime) return false;
      const localMatch = dayjs(x.timeInCentralDateTime).format("YYYY-MM-DD") === selectedDate;
      const rawPrefixMatch = x.timeInCentralDateTime.startsWith(selectedDate);
      return localMatch || rawPrefixMatch;
    });
    setLogs(filtered);
  }, [getAttendanceLogEntries.data, selectedDate]);

  // Keep ref in sync so the FormProvider save handler can read it
  logsRef.current = logs;

  const handleAttendanceLogUpdate = (childId: number, log: AttendanceLogEntry | null) => {
    setLogs((prev) => {
      const filtered = prev.filter((x) => x.childId !== childId);
      return log ? [...filtered, log] : filtered;
    });
  };

  return (
    <Stack gap={2} flexGrow={1}>
      <Stack sx={{ alignItems: { xs: "center", md: "flex-start", width: "100%" } }} flex={1}>
        {isFetching && (
          <Stack direction="row" sx={{ width: "100%", justifyContent: "center", pt: 4 }}>
            <CircularProgress />
          </Stack>
        )}
        {!isFetching && (
          <Stack sx={{ width: "100%" }} gap={2}>
            {childrenToList.map((child) => {
              const log = logs.find((x) => x.childId === child.id);
              return <AttendanceLogEntryEditor key={child.id} child={child} log={log} date={date} onChange={handleAttendanceLogUpdate} />;
            })}
          </Stack>
        )}
      </Stack>

      <Button
        variant="contained"
        onClick={form.save}
        disabled={form.isDisabled || isFetching || form.isLoading}
        sx={{ height: 50, width: { xs: "100%", md: "inherit" } }}
        startIcon={<FontAwesomeIcon icon={faSave} />}
      >
        {form.isLoading ? "Saving..." : "Save Changes"}
      </Button>
      <Button variant="outlined" onClick={form.cancel} sx={{ height: 50, width: { xs: "100%", md: "inherit" } }}>
        Cancel
      </Button>
    </Stack>
  );
}
